import React from 'react';
import {
  AppLayout,
  Button,
  ColumnLayout,
  Header,
  SpaceBetween,
  Container,
  Link,
  HelpPanel,
  Box,
  Select,
  FormField,
} from '@cloudscape-design/components';
import ServiceNavigation from '../ServiceNavigation.jsx';
import Breadcrumbs from '../Breadcrumbs.jsx';
import ExtendingPanel from '../ExtendingPanel.jsx';
import SynopsisPanel from '../SynopsisPanel.jsx';
import BrowserCheck from '../BrowserCheck';
import { sendAnalytics } from '../../resources/rum-provider';
import { EDGE_FUNCTIONS_NORMALIZE_CF_ENDPOINT } from '../../resources/prod-env.jsx';
import '../../styles/form.scss';

const demoModule = "url-normalization";

const URL_VARIATIONS = [
  { label: '1 — /products?category=landscape&sort=popular', value: '/products?category=landscape&sort=popular', desc: 'Original order' },
  { label: '2 — /products?sort=popular&category=landscape', value: '/products?sort=popular&category=landscape', desc: 'Reversed parameter order' },
  { label: '3 — /products?sort=POPULAR&category=LANDSCAPE', value: '/products?sort=POPULAR&category=LANDSCAPE', desc: 'Mixed case values' },
  { label: '4 — /PRODUCTS?CATEGORY=landscape&SORT=popular', value: '/PRODUCTS?CATEGORY=landscape&SORT=popular', desc: 'Uppercase path and keys' },
  { label: '5 — /products?sort=popular&category=landscape&sort=popular', value: '/products?sort=popular&category=landscape&sort=popular', desc: 'Duplicate parameter' },
];

export default class UrlNormalization extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      toolsIndex: 0,
      toolsOpen: false,
      selectedVariation: URL_VARIATIONS[0],
      iframeKey: 0,
    };
  }

  replaceToolsContent = (index) => {
    this.setState({ toolsIndex: index, toolsOpen: true });
  };

  handleTest = () => {
    sendAnalytics({ demo: demoModule });
    this.setState(prev => ({ iframeKey: prev.iframeKey + 1 }));
  };

  render() {
    return (
      <AppLayout
        navigation={<ServiceNavigation />}
        breadcrumbs={<Breadcrumbs items={BreadcrumbsItems} />}
        content={
          <Content
            replaceToolsContent={this.replaceToolsContent}
            handleTest={this.handleTest}
            state={this.state}
            setState={(s) => this.setState(s)}
          />
        }
        tools={Tools[this.state.toolsIndex]}
        toolsOpen={this.state.toolsOpen}
        onToolsChange={({ detail }) => this.setState({ toolsOpen: detail.open })}
        contentType="default"
      />
    );
  }
}

const Content = ({ replaceToolsContent, handleTest, state, setState }) => {
  const fullUrl = EDGE_FUNCTIONS_NORMALIZE_CF_ENDPOINT + state.selectedVariation.value;
  const normalizedUrl = EDGE_FUNCTIONS_NORMALIZE_CF_ENDPOINT + '/products/index.html?category=landscape&sort=popular';

  return (
    <React.Fragment>
      <BrowserCheck />
      <SpaceBetween direction="vertical" size="l">
        <SynopsisPanel videolink="https://broadcast.amazon.com/videos/2001107">
          <SpaceBetween direction="vertical" size="s">
            <Box>
              <Box variant="strong">CloudFront Function &lt; 1ms</Box> — Normalizes URLs before the cache lookup to maximize cache hit ratio.
              Multiple URL variations that point to the same content (different parameter order, mixed case, duplicates) all resolve to a single cache key.
            </Box>
            <Box variant="strong">Architecture</Box>
            <div style={{ background: '#f8f8f8', border: '1px solid #d5dbdb', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.75rem', minWidth: 650 }}>
                <div style={{ textAlign: 'center', minWidth: 80 }}><div style={{ fontSize: '2rem' }}>👤</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>User</div><div style={{ fontSize: '0.65rem', color: '#545b64' }}>/PRODUCTS?Sort=POPULAR</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 90 }}><div style={{ fontSize: '2rem' }}>☁️</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>CloudFront</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>Viewer Request</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 120, border: '2px solid #FF9900', borderRadius: 10, padding: '0.5rem', background: 'rgba(255,153,0,0.06)' }}><div style={{ fontSize: '2rem' }}>⚡</div><div style={{ fontWeight: 700, fontSize: '0.85rem', color: '#FF9900' }}>CF Function</div><div style={{ fontSize: '0.65rem', color: '#545b64' }}>Normalize URL</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 90 }}><div style={{ fontSize: '2rem' }}>☁️</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Cache Lookup</div><div style={{ fontSize: '0.7rem', color: '#1d8102' }}>Single cache key</div></div>
                <div style={{ color: '#146eb4', fontWeight: 'bold', fontSize: '1.3rem' }}>→</div>
                <div style={{ textAlign: 'center', minWidth: 70 }}><div style={{ fontSize: '2rem' }}>🪣</div><div style={{ fontWeight: 700, fontSize: '0.85rem' }}>Amazon S3</div><div style={{ fontSize: '0.7rem', color: '#545b64' }}>Origin</div></div>
              </div>
            </div>
            <Box variant="strong">CloudFront Function code</Box>
            <div style={{ background: '#0d1117', borderRadius: 8, padding: '1rem', overflowX: 'auto' }}>
              <pre style={{ margin: 0, color: '#c9d1d9', fontSize: '0.82rem', lineHeight: 1.6, fontFamily: "'Courier New', monospace" }}>{`function handler(event) {
    var request = event.request;
    var uri = request.uri.toLowerCase();
    var qs = request.querystring;

    // Sort params alphabetically, lowercase, deduplicate
    if (qs && Object.keys(qs).length > 0) {
        var sorted = {};
        var keys = Object.keys(qs).sort();
        for (var i = 0; i < keys.length; i++) {
            var key = keys[i].toLowerCase();
            if (!sorted[key]) {
                sorted[key] = { value: (qs[keys[i]].value || '').toLowerCase() };
            }
        }
        request.querystring = sorted;
    }

    // Resolve directories to index.html
    if (uri.indexOf('.') === -1) {
        if (!uri.endsWith('/')) uri += '/';
        uri += 'index.html';
    }
    request.uri = uri;
    return request;
}`}</pre>
            </div>
            <Box variant="strong">Comparison</Box>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
              <thead>
                <tr style={{ borderBottom: '2px solid #d5dbdb', textAlign: 'left' }}>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Approach</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Cache Entries</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Cache Hit Ratio</th>
                  <th style={{ padding: '0.4rem 0.6rem' }}>Latency</th>
                </tr>
              </thead>
              <tbody>
                <tr style={{ borderBottom: '1px solid #eee' }}><td style={{ padding: '0.4rem 0.6rem' }}>Without normalization</td><td style={{ padding: '0.4rem 0.6rem' }}>5 entries (same content)</td><td style={{ padding: '0.4rem 0.6rem' }}>~15%</td><td style={{ padding: '0.4rem 0.6rem' }}>50-200ms</td></tr>
                <tr><td style={{ padding: '0.4rem 0.6rem', fontWeight: 600 }}>With CloudFront Function</td><td style={{ padding: '0.4rem 0.6rem' }}>1 entry</td><td style={{ padding: '0.4rem 0.6rem' }}>~55% (+40%)</td><td style={{ padding: '0.4rem 0.6rem' }}>&lt; 1ms</td></tr>
              </tbody>
            </table>
          </SpaceBetween>
        </SynopsisPanel>

        <Container header={<Header variant="h2">Test Live — Real request through CloudFront</Header>}>
          <SpaceBetween direction="vertical" size="s">
            <FormField label="Select a URL variation — all point to the same content but with different parameter order, case, or duplicates">
              <Select
                selectedOption={state.selectedVariation}
                onChange={({ detail }) => setState({ selectedVariation: detail.selectedOption })}
                options={URL_VARIATIONS}
              />
            </FormField>
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', flexWrap: 'wrap' }}>
              <Button variant="primary" onClick={handleTest} iconName="refresh">
                Send request to CloudFront
              </Button>
            </div>
            <div style={{ background: '#f0f0f0', borderRadius: 6, padding: '0.6rem 0.8rem', fontSize: '0.85rem' }}>
              <div><Box variant="strong">Original URL:</Box> <Box variant="code" display="inline">{fullUrl}</Box></div>
              <div style={{ marginTop: '0.3rem' }}><Box variant="strong">Normalized to:</Box> <Box variant="code" display="inline">{normalizedUrl}</Box></div>
              <div style={{ marginTop: '0.3rem', color: '#1d8102', fontWeight: 600 }}>
                ↑ All 5 variations normalize to the same cache key. Check <code>X-Normalized-URI</code> and <code>X-Cache</code> headers in DevTools.
              </div>
            </div>
            <div style={{ border: '2px solid #d5dbdb', borderRadius: 8, overflow: 'hidden', background: '#fff' }}>
              <iframe
                key={state.iframeKey}
                src={fullUrl}
                title="URL Normalization Result"
                style={{ width: '100%', height: 300, border: 'none' }}
              />
            </div>
          </SpaceBetween>
        </Container>

        <ExtendingPanel label="Extend it">
          <ColumnLayout columns={2} variant="text-grid">
            <Box>
              Questions/ideas on this demo? <Box variant="strong">Daniel Martinez, dmrubio@amazon.es</Box>
            </Box>
            <Box>
              <Link external href="https://docs.aws.amazon.com/AmazonCloudFront/latest/DeveloperGuide/example-function-normalize-query-string.html">
                📘 CloudFront Functions documentation
              </Link>
            </Box>
          </ColumnLayout>
        </ExtendingPanel>
      </SpaceBetween>
    </React.Fragment>
  );
};

const BreadcrumbsItems = [
  { text: 'Home', href: '/' },
  { text: 'URL Normalization', href: '/urlnormalization' },
];

const Tools = [
  <HelpPanel header={<h3>URL Normalization</h3>}>
    <p>This demo sends real HTTP requests to a CloudFront distribution. A CloudFront Function at viewer-request
    normalizes the URL before the cache lookup — sorting parameters, lowercasing, and deduplicating.</p>
    <p>The result is that all URL variations resolve to the same cache key, dramatically improving cache hit ratio.</p>
  </HelpPanel>,
];
